import { Button, Empty, Icon, Panel, Table, type ColumnDef } from '../design/index.js'
import type { DocumentDetail } from '../app/api.js'
import { formatDate } from './format.js'

/**
 * What this document used to say (docs/02 §4, docs/06 §4).
 *
 * A purchase order that is amended keeps its number, so the version a supplier
 * accepted has to stay readable — otherwise "we ordered 40, you sent 60" has
 * no evidence on either side. Each row is the document as it stood when it was
 * reopened, with the reason the person gave for reopening it.
 */

type Row = DocumentDetail['revisions'][number]

const columns: ReadonlyArray<ColumnDef<Row>> = [
  { key: 'revisionNo', header: 'Revision', width: 110, render: (r) => `Rev ${r.revisionNo}` },
  { key: 'documentStatus', header: 'Status', type: 'status', width: 150 },
  { key: 'lineCount', header: 'Lines', align: 'right', width: 90 },
  { key: 'total', header: 'Value', type: 'money', width: 160 },
  { key: 'createdBy', header: 'Superseded by', width: 200, render: (r) => r.createdBy ?? '—' },
  { key: 'createdAt', header: 'On', width: 150, render: (r) => formatDate(r.createdAt) },
  { key: 'reason', header: 'Reason given', render: (r) => r.reason ?? '—' },
]

export const RevisionsTab = ({
  doc, docLabel, onRevise,
}: {
  doc: DocumentDetail
  docLabel?: string
  /** Offered only while the document is approved and the person may reopen it. */
  onRevise?: () => void
}) => (
  <Panel title={`Earlier versions — revised ${doc.revisionNo} time${doc.revisionNo === 1 ? '' : 's'}`}>
    {onRevise ? (
      <div className="row" style={{ marginBottom: 14 }}>
        <Button kind="primary" icon={<Icon name="plus" size={18} />} onClick={onRevise}>
          Revise the {(docLabel ?? 'document').toLowerCase()}
        </Button>
      </div>
    ) : null}
    <Table
      columns={columns}
      rows={doc.revisions}
      getKey={(r) => r.id}
      empty={
        <Empty
          title="No revisions"
          body={[
            doc.documentStatus === 'APPROVED'
              ? 'This document has not been reopened since it was approved.'
              : 'A revision is kept each time an approved document is reopened for changes.',
            'The version on screen is always the current one.',
          ]}
        />
      }
    />
  </Panel>
)
